import { buildQuestions, seedTargets } from './data';
import { isChoice, notifyStructure, state } from './state';
import {
  DEFAULT_CLOSING_PROMPT,
  DEFAULT_OPENING_PROMPT,
  DEFAULT_TARGET_TEMPLATE,
} from './templates';
import type {
  AnswerPrompt,
  Area,
  BaseImage,
  ChoiceQuestion,
  CropArea,
  PhotoQuestion,
  RandomMode,
  Target,
} from './types';

const STORAGE_KEY = 'espolon-presets';

export interface PresetOption {
  label: string;
  injects: boolean;
  prompts: AnswerPrompt[];
}

export interface PresetQuestion {
  kind: 'choice' | 'photo';
  id: string;
  heading?: string;
  hint?: string;
  title: string;
  // choice-specific
  promptLabel?: string;
  colorIndex?: 0 | 1 | 2 | 3;
  maxSelections?: number;
  options?: PresetOption[];
  selected?: number | number[] | null;
  // photo-specific
  description?: string;
  prompts?: AnswerPrompt[];
  // both
  randomMode?: RandomMode;
}

export interface PresetData {
  id: string;
  version: 3;
  name: string;
  savedAt: string;
  openingPrompt: string;
  closingPrompt: string;
  targetTemplate: string;
  targets: Target[];
  areas: Area[];
  cropArea: CropArea | null;
  baseImage: BaseImage | null;
  questions: PresetQuestion[];
}

export function captureCurrentState(name: string): PresetData {
  const questions: PresetQuestion[] = state.questions.map((q) => {
    if (isChoice(q)) {
      return {
        kind: 'choice',
        id: q.id,
        heading: q.heading,
        hint: q.hint,
        title: q.title,
        promptLabel: q.promptLabel,
        colorIndex: q.colorIndex,
        maxSelections: q.maxSelections,
        options: q.options.map((o) => ({
          label: o.label,
          injects: o.injects,
          prompts: o.prompts.map((p) => ({
            id: p.id,
            alternatives: p.alternatives.map((a) => ({ ...a })),
          })),
        })),
        selected: [...q.selected],
        randomMode: q.randomMode,
      };
    }
    return {
      kind: 'photo',
      id: q.id,
      heading: q.heading,
      hint: q.hint,
      title: q.title,
      description: q.description,
      prompts: q.prompts.map((p) => ({
        id: p.id,
        alternatives: p.alternatives.map((a) => ({ ...a })),
      })),
      randomMode: q.randomMode,
    };
  });

  return {
    id: `preset-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    version: 3,
    name: name.trim() || 'Untitled Preset',
    savedAt: new Date().toISOString(),
    openingPrompt: state.openingPrompt,
    closingPrompt: state.closingPrompt,
    targetTemplate: state.targetTemplate,
    targets: state.targets.map((t) => ({ ...t })),
    areas: state.areas.map((a) => ({
      ...a,
      targets: a.targets.map((t) => ({ ...t })),
      extraImages: a.extraImages.map((ei) => ({ ...ei })),
    })),
    cropArea: state.cropArea ? { ...state.cropArea } : null,
    baseImage: state.baseImage ? { ...state.baseImage } : null,
    questions,
  };
}

export function savePreset(name: string): PresetData {
  const preset = captureCurrentState(name);
  const existing = loadPresets();
  existing.unshift(preset);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  return preset;
}

export function loadPresets(): PresetData[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidPreset);
  } catch {
    return [];
  }
}

export function deletePreset(id: string): void {
  const remaining = loadPresets().filter((p) => p.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));
}

export function applyPreset(preset: PresetData): void {
  state.openingPrompt = preset.openingPrompt ?? DEFAULT_OPENING_PROMPT;
  state.closingPrompt = preset.closingPrompt ?? DEFAULT_CLOSING_PROMPT;
  state.targetTemplate = preset.targetTemplate ?? DEFAULT_TARGET_TEMPLATE;

  const defaultTargets = seedTargets();
  const defaultPriority = new Map<string, string[]>();
  for (const t of defaultTargets) defaultPriority.set(t.name.toLowerCase(), t.priority);

  state.targets = (preset.targets ?? defaultTargets).map((t) => ({
    ...t,
    priority: Array.isArray(t.priority) && t.priority.length > 0
      ? [...t.priority]
      : [...(defaultPriority.get(t.name.toLowerCase()) ?? [])],
  }));
  state.areas = (preset.areas ?? []).map((a) => ({
    ...a,
    includeBase: a.includeBase ?? true,
    hidden: a.hidden ?? false,
    targets: a.targets.map((t) => ({ ...t })),
    extraImages: (a.extraImages ?? []).map((ei) => ({ ...ei })),
  }));
  state.cropArea = preset.cropArea ? { ...preset.cropArea } : null;
  state.baseImage = preset.baseImage ? { ...preset.baseImage } : null;

  const defaultQuestions = buildQuestions();

  state.questions = preset.questions.map((pq) => {
    if (pq.kind === 'choice') {
      return {
        kind: 'choice',
        id: pq.id,
        heading: pq.heading ?? '',
        hint: pq.hint ?? '',
        title: pq.title,
        promptLabel: pq.promptLabel ?? '',
        colorIndex: pq.colorIndex ?? 0,
        maxSelections: Math.max(1, pq.maxSelections ?? 1),
        options: (pq.options ?? []).map((o) => ({
          label: o.label,
          injects: o.injects,
          prompts: (o.prompts ?? []).map((p) => ({
            id: p.id,
            alternatives: p.alternatives.map((a) => ({ ...a })),
          })),
        })),
        selected: normalizeSelected(pq.selected),
        randomMode: pq.randomMode === 'cycle' ? 'cycle' : 'shuffle',
        cycleIndex: 0,
      } satisfies ChoiceQuestion;
    }
    const defaultPhoto = defaultQuestions.find(
      (q) => q.id === pq.id && q.kind === 'photo',
    ) as PhotoQuestion | undefined;
    return {
      kind: 'photo',
      id: pq.id,
      heading: pq.heading ?? defaultPhoto?.heading ?? '',
      hint: pq.hint ?? defaultPhoto?.hint ?? '',
      title: pq.title,
      description: pq.description ?? defaultPhoto?.description ?? '',
      file: null,
      prompts: (pq.prompts ?? []).map((p) => ({
        id: p.id,
        alternatives: p.alternatives.map((a) => ({ ...a })),
      })),
      randomMode: pq.randomMode === 'cycle' ? 'cycle' : 'shuffle',
      cycleIndex: 0,
    } satisfies PhotoQuestion;
  });

  notifyStructure();
}

export function downloadPreset(preset: PresetData): void {
  const json = JSON.stringify(preset, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const slug = preset.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  a.download = `${slug}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function importPresetFromFile(file: File): Promise<PresetData> {
  const text = await file.text();
  const parsed: unknown = JSON.parse(text);
  if (!isValidPreset(parsed)) {
    throw new Error('File does not appear to be a valid preset.');
  }
  return { ...parsed, id: `preset-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
}

function normalizeSelected(value: unknown): number[] {
  if (Array.isArray(value)) return value.filter((v): v is number => typeof v === 'number');
  if (typeof value === 'number') return [value];
  return [];
}

function isValidPreset(value: unknown): value is PresetData {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Record<string, unknown>;
  return (
    typeof p.id === 'string' &&
    p.version === 3 &&
    typeof p.name === 'string' &&
    typeof p.savedAt === 'string' &&
    Array.isArray(p.questions)
  );
}
